import { useCallback, useContext, useEffect, useState } from "react";
import { CoreFilterButton, CoreFilterSectionOption } from "@verity/components/core";
import { extractAssessmentName } from "../../../helpers/assessment-name";
import { PortalControllerContext } from "../../context";
import i18n from "../../../i18n";
import { isStandaloneAssessment, QuizWithProctoringOptions } from "./assessments-page-helper";

const ALL_ASSESSMENT_TYPES = ["lms", "third-party", "standalone"] as const;
export type AssessmentType = (typeof ALL_ASSESSMENT_TYPES)[number];
const ALL_ASSESSMENT_PLATFORMS = ["extension", "lockdown", "mobile", "chromeOs", "notProctored"] as const;
export type AssessmentPlatform = (typeof ALL_ASSESSMENT_PLATFORMS)[number];
const ALL_PROCTORING_METHODS = ["automated", "live", "pop-in"] as const;
export type ProctoringMethod = (typeof ALL_PROCTORING_METHODS)[number];

export type AssessmentFilter = {
	type: AssessmentType[];
	platform: AssessmentPlatform[];
	method: ProctoringMethod[];
};

export function AssessmentFilterButton({
	filter,
	setFilter,
	typeOptions = ALL_ASSESSMENT_TYPES,
	platformOptions = ALL_ASSESSMENT_PLATFORMS,
	methodOptions = ALL_PROCTORING_METHODS,
	alternateIcon,
}: {
	filter: AssessmentFilter;
	setFilter: (newFilter: AssessmentFilter) => void;
	typeOptions?: Readonly<AssessmentType[]>;
	platformOptions?: Readonly<AssessmentPlatform[]>;
	methodOptions?: Readonly<ProctoringMethod[]>;
	alternateIcon?: boolean;
}) {
	const { portalContext, verityInstituteSettings } = useContext(PortalControllerContext);
	const { lms } = portalContext;
	const filterAllowedTypes = useCallback(
		(types: Readonly<AssessmentType[]>) =>
			types.filter((type) => {
				switch (type) {
					case "lms":
					case "third-party":
						return lms;
					default:
						return true;
				}
			}),
		[lms],
	);
	const filterAllowedPlatforms = useCallback(
		(platforms: Readonly<AssessmentPlatform[]>) =>
			platforms.filter((platform) => {
				switch (platform) {
					case "extension":
						return verityInstituteSettings.allowExtensionPlatform;
					case "lockdown":
					case "chromeOs":
						return verityInstituteSettings.allowLockdownPlatform;
					case "mobile":
						return verityInstituteSettings.allowMobilePlatform;
					// institution cant prevent LMS quizzes
					case "notProctored":
						return true;
				}
			}),
		[verityInstituteSettings],
	);
	const filterAllowedMethods = useCallback(
		(methods: Readonly<ProctoringMethod[]>) =>
			methods.filter((method) => {
				switch (method) {
					case "live":
						return !verityInstituteSettings.liteMode && verityInstituteSettings.allowLiveProctoring;
					case "pop-in":
						return !verityInstituteSettings.liteMode && verityInstituteSettings.allowLivePopIn;
					case "automated":
						return true;
				}
			}),
		[verityInstituteSettings],
	);

	const [typeFilter, setTypeFilter] = useState<AssessmentType[]>(filterAllowedTypes(filter.type));
	const [platformFilter, setPlatformFilter] = useState<AssessmentPlatform[]>(filterAllowedPlatforms(filter.platform));
	const [methodFilter, setMethodFilter] = useState<ProctoringMethod[]>(filterAllowedMethods(filter.method));

	useEffect(() => {
		setTypeFilter((filter) => filterAllowedTypes(filter));
	}, [lms]);

	useEffect(() => {
		setPlatformFilter((filter) => filterAllowedPlatforms(filter));
		setMethodFilter((filter) => filterAllowedMethods(filter));
	}, [verityInstituteSettings]);

	const count = typeFilter.length + platformFilter.length + methodFilter.length;

	const allowedTypeOptions = filterAllowedTypes(typeOptions);
	const allowedPlatformOptions = filterAllowedPlatforms(platformOptions);
	const allowedMethodOptions = filterAllowedMethods(methodOptions);
	const filterSections: CoreFilterSectionOption[] = [];
	if (allowedTypeOptions.length > 1) {
		filterSections.push({
			alignment: "top",
			sectionTitle: i18n.t("portal.components.assessmentPageTSX.assessmentType.header"),
			checkboxes: allowedTypeOptions.map((type) => ({
				checked: typeFilter.includes(type),
				onChange: (checked) => {
					const newTypes = typeFilter.filter((t) => t !== type);
					setTypeFilter(checked ? newTypes.concat([type]) : newTypes);
				},
				children: i18n.t(`portal.components.assessmentPageTSX.assessmentType.${type}`),
			})),
		});
	}
	if (allowedPlatformOptions.length > 1) {
		filterSections.push({
			alignment: "top",
			sectionTitle: i18n.t("portal.components.assessmentPageTSX.platform.header"),
			checkboxes: allowedPlatformOptions.map((platform) => ({
				checked: platformFilter.includes(platform),
				onChange: (checked) => {
					const newPlatforms = platformFilter.filter((p) => p !== platform);
					setPlatformFilter(checked ? newPlatforms.concat([platform]) : newPlatforms);
				},
				children: i18n.t(`portal.components.assessmentPageTSX.platform.${platform}`),
			})),
		});
	}
	if (allowedMethodOptions.length > 1) {
		filterSections.push({
			alignment: "top",
			sectionTitle: i18n.t("portal.components.assessmentPageTSX.methods"),
			checkboxes: allowedMethodOptions.map((method) => ({
				checked: methodFilter.includes(method),
				onChange: (checked) => {
					const newMethods = methodFilter.filter((m) => m !== method);
					setMethodFilter(checked ? newMethods.concat([method]) : newMethods);
				},
				children: i18n.t(`portal.components.assessmentPageTSX.columnList.method.${method}`),
			})),
		});
	}

	return (
		<CoreFilterButton
			filterCount={count}
			onClear={() => {
				setTypeFilter([]);
				setPlatformFilter([]);
				setMethodFilter([]);
			}}
			onApply={() => setFilter({ type: [...typeFilter], platform: [...platformFilter], method: [...methodFilter] })}
			filterSections={filterSections}
			alternateIcon={alternateIcon}
		/>
	);
}

export function assessmentMatches({ type, method, platform }: AssessmentFilter, assessment: QuizWithProctoringOptions) {
	const { proctoringOptions } = assessment;
	const quizName = extractAssessmentName(assessment);

	let assessmentType: AssessmentType = "lms";
	if (quizName.startsWith("Third Party: ")) {
		assessmentType = "third-party";
	}
	if (isStandaloneAssessment(assessment)) {
		assessmentType = "standalone";
	}

	const assessmentPlatforms = new Set<AssessmentPlatform>();
	if (proctoringOptions?.isExtension) {
		assessmentPlatforms.add("extension");
	}
	if (proctoringOptions?.isLockdown) {
		assessmentPlatforms.add("lockdown");
		if (proctoringOptions?.chromeOsAccess) {
			assessmentPlatforms.add("chromeOs");
		}
	}
	if (proctoringOptions?.isMobile) {
		assessmentPlatforms.add("mobile");
	}
	if (!proctoringOptions?.enabled) {
		assessmentPlatforms.add("notProctored");
	}

	let assessmentMethod: ProctoringMethod = "automated";
	if (proctoringOptions?.isLiveProctored) {
		assessmentMethod = "live";
	} else if (proctoringOptions?.isLivePopIn) {
		assessmentMethod = "pop-in";
	}

	return (
		(type.length === 0 || type.includes(assessmentType)) &&
		(platform.length === 0 || platform.some((p) => assessmentPlatforms.has(p))) &&
		(method.length === 0 || method.includes(assessmentMethod))
	);
}

export function hasAssessmentFilter({ type, platform, method }: AssessmentFilter) {
	return type.length > 0 || platform.length > 0 || method.length > 0;
}
